import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import * as React from "react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Tailwind-aligned breakpoints (px, min-width). */
export const BP = {
  xs: 425,
  sm: 640,
  md: 768,   // tablet boundary
  lg: 1024,
  xl: 1280,  // desktop boundary
  "2xl": 1536,
} as const;
export type Breakpoint = keyof typeof BP;

/**
 * useBreakpoint — returns 'mobile' | 'tablet' | 'desktop' for distinct layouts.
 *  - mobile  : < md (768)
 *  - tablet  : md – xl (768 – 1279)
 *  - desktop : ≥ xl (1280)
 */
export type Device = "mobile" | "tablet" | "desktop";
export function useDevice(): Device {
  const get = (): Device => {
    if (typeof window === "undefined") return "desktop";
    const w = window.innerWidth;
    if (w < BP.md) return "mobile";
    if (w < BP.xl) return "tablet";
    return "desktop";
  };
  const [device, setDevice] = React.useState<Device>(get);
  React.useEffect(() => {
    const onResize = () => setDevice(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return device;
}

/** Match an arbitrary media query reactively. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** Format a number with locale + optional max fraction digits. */
export function formatNumber(n: number, opts?: Intl.NumberFormatOptions) {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, ...opts }).format(n);
}

/** Compact relative time ("2h ago", "Yesterday"). */
export function relativeTime(date: Date | string | number): string {
  const d = typeof date === "object" ? date : new Date(date);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 2) return "yesterday";
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Today's date as a LOCAL `YYYY-MM-DD`. Use this for date-input defaults and
 * `max` constraints instead of `new Date().toISOString().slice(0, 10)`, which
 * returns the UTC day — that's the PREVIOUS day for users ahead of UTC in the
 * early hours (e.g. 04:00 IST is still "yesterday" in UTC), so an inspection
 * created on the 26th in India defaulted to (and was capped at) the 25th.
 */
export function todayLocal(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Format a calendar date ("25 Jun 2026") for a date the user picked — e.g. an
 * inspection's dateOfInspection. Rendered in the viewer's local timezone (not
 * relativeTime(), which would report "23h ago" for a date set to today).
 */
export function formatDate(date: Date | string | number | null | undefined): string {
  if (date == null || date === "") return "—";
  const d = typeof date === "object" ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}
